import { Loader2, Check, AlertCircle } from "lucide-react";
import { type ArtifactType } from "~/components/assistant/messages/artifactFormats";
import { Button } from "~/components/ui/button";

function getArtifactLabel(artifactType?: ArtifactType): string {
  if (artifactType === "figure") return "chart";
  if (artifactType === "dataframe") return "table";
  return "file";
}

interface ExportProgressProps {
  status: "exporting" | "success" | "error";
  artifactType?: ArtifactType;
  fileName?: string;
  workbenchName?: string;
  workbenchPath?: string;
  error?: string;
  onClose: () => void;
  onRetry?: () => void;
}

export function ExportProgress({
  status,
  artifactType,
  fileName,
  workbenchName,
  workbenchPath,
  error,
  onClose,
  onRetry,
}: ExportProgressProps) {
  if (status === "exporting") {
    return (
      <div className="flex flex-col items-center justify-center py-8 gap-4">
        <Loader2 className="size-10 text-primary animate-spin" />
        <p className="text-base font-medium">Exporting...</p>
      </div>
    );
  }

  if (status === "success") {
    const artifactLabel = getArtifactLabel(artifactType);
    return (
      <div className="flex flex-col items-center justify-center py-8 gap-4">
        <div className="rounded-full bg-green-100 p-5">
          <Check className="size-8 text-green-600" />
        </div>
        <div className="text-center space-y-1">
          <p className="text-base">
            Your {artifactLabel} has been sent to the workbench{" "}
            <span className="font-bold">{workbenchName}</span>
          </p>
          <p className="text-sm text-gray-600">It may take a few minutes to appear.</p>
        </div>
        {fileName && (
          <div className="text-center mt-1">
            <p className="text-sm font-semibold">File name</p>
            <p className="text-sm text-gray-600 font-mono">{fileName}</p>
          </div>
        )}
        <div className="flex flex-col items-center mt-2 gap-1">
          {workbenchPath && (
            <Button asChild className="px-6">
              <a href={workbenchPath} onClick={onClose}>
                Go to Workbench
              </a>
            </Button>
          )}
          <Button variant="link" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    );
  }

  if (status === "error") {
    const artifactLabel = getArtifactLabel(artifactType);
    return (
      <div className="flex flex-col items-center justify-center py-8 gap-4">
        <div className="rounded-full bg-red-100 p-5">
          <AlertCircle className="size-8 text-red-600" />
        </div>
        <div className="text-center space-y-1">
          <p className="text-lg font-bold">Unable to export {artifactLabel}</p>
          <p className="text-sm text-gray-600">
            {error || "Something went wrong while exporting your file."}
          </p>
        </div>
        <div className="flex flex-col items-center mt-2 gap-1">
          {onRetry && (
            <Button onClick={onRetry} className="px-6">
              Retry
            </Button>
          )}
          <Button variant="link" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  return null;
}
